import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardLayout from "./components/DashboardLayout";
import Dashboard from "./pages/Dashboard";
import PatientsPage from "./pages/PatientsPage";
import PatientDetailPage from "./pages/PatientDetailPage";
import FamiliesPage from "./pages/FamiliesPage";
import AssessmentsPage from "./pages/AssessmentsPage";
import NewAssessmentPage from "./pages/NewAssessmentPage";
import EvaluationsPage from "./pages/EvaluationsPage";
import ProgressNotesPage from "./pages/ProgressNotesPage";
import CalendarPage from "./pages/CalendarPage";
import StaffPage from "./pages/StaffPage";
import HRPage from "./pages/HRPage";
import UnifiedFinancePage from "./pages/UnifiedFinancePage";
import AccountsExcelPage from "./pages/AccountsExcelPage";
import UKPayrollPage from "./pages/UKPayrollPage";
import MoneysoftPayrollPage from "./pages/MoneysoftPayrollPage";
import RTIDashboardPage from "./pages/RTIDashboardPage";
import InvoicesPage from "./pages/InvoicesPage";
import MyEarningsPage from "./pages/MyEarningsPage";
import MessagingPage from "./pages/MessagingPage";
import AuditLogsPage from "./pages/AuditLogsPage";
import SettingsPage from "./pages/SettingsPage";
import UserManagementPage from "./pages/UserManagementPage";
import EventsPage from "./pages/EventsPage";
import ProfessionalsPage from "./pages/ProfessionalsPage";
import AccessManagementPage from "./pages/AccessManagementPage";
import FormConfigPage from "./pages/FormConfigPage";
import MyHRPage from "./pages/MyHRPage";
import MyProfilePage from "./pages/MyProfilePage";
import SafeguardingPage from "./pages/SafeguardingPage";
import ParentPortalPage from "./pages/ParentPortalPage";
import AnalyticsDashboardPage from "./pages/AnalyticsDashboardPage";
import DataManagementPage from "./pages/DataManagementPage";
import PolicyTemplatesPage from "./pages/PolicyTemplatesPage";
import AttendancePage from "./pages/AttendancePage";
import AIWorkforcePage from "./pages/AIWorkforcePage";
import AIAssistantPage from "./pages/AIAssistantPage";
import CRMDashboardPage from "./pages/CRMDashboardPage";
import DBSTrackingPage from "./pages/DBSTrackingPage";
import AIPresentationPage from "./pages/AIPresentationPage";
import MeetingsPage from "./pages/MeetingsPage";
import MeetingRoomPage from "./pages/MeetingRoomPage";
import MeetingMinutesPage from "./pages/MeetingMinutesPage";
import MeetingRecordsPage from "./pages/MeetingRecordsPage";
import RotaManagementPage from "./pages/RotaManagementPage";
import ParentDashboardPage from "./pages/ParentDashboardPage";
import ParentRegisterPage from "./pages/ParentRegisterPage";
import ParentInvitePage from "./pages/ParentInvitePage";
import ParentManagementPage from "./pages/ParentManagementPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import FeedbackPage from "./pages/FeedbackPage";
import FeedbackManagementPage from "./pages/FeedbackManagementPage";
import PolicyPage from "./pages/PolicyPage";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading ICRCL...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Parent Portal Public Routes */}
          <Route path="/parent-register" element={<ParentRegisterPage />} />
          <Route path="/parent-invite/:token" element={<ParentInvitePage />} />
          {/* Public Feedback Form (QR Code) */}
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="patients" element={<PatientsPage />} />
            <Route path="patients/:id" element={<PatientDetailPage />} />
            <Route path="families" element={<FamiliesPage />} />
            <Route path="assessments" element={<AssessmentsPage />} />
            <Route path="assessments/new" element={<NewAssessmentPage />} />
            <Route path="assessments/new/:patientId" element={<NewAssessmentPage />} />
            <Route path="evaluations" element={<EvaluationsPage />} />
            <Route path="progress-notes" element={<ProgressNotesPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="staff" element={<StaffPage />} />
            <Route path="hr" element={<HRPage />} />
            <Route path="users" element={<UserManagementPage />} />
            <Route path="finance" element={<UnifiedFinancePage />} />
            <Route path="finance/accounts" element={<AccountsExcelPage />} />
            <Route path="payroll" element={<MoneysoftPayrollPage />} />
            <Route path="payroll/legacy" element={<UKPayrollPage />} />
            <Route path="rti" element={<RTIDashboardPage />} />
            <Route path="invoices" element={<InvoicesPage />} />
            <Route path="my-earnings" element={<MyEarningsPage />} />
            <Route path="messages" element={<MessagingPage />} />
            <Route path="events" element={<EventsPage />} />
            <Route path="professionals" element={<ProfessionalsPage />} />
            <Route path="audit" element={<AuditLogsPage />} />
            <Route path="access-management" element={<AccessManagementPage />} />
            <Route path="form-config" element={<FormConfigPage />} />
            <Route path="my-hr" element={<MyHRPage />} />
            <Route path="my-profile" element={<MyProfilePage />} />
            <Route path="safeguarding" element={<SafeguardingPage />} />
            <Route path="analytics" element={<AnalyticsDashboardPage />} />
            <Route path="data-management" element={<DataManagementPage />} />
            <Route path="policy-templates" element={<PolicyTemplatesPage />} />
            <Route path="attendance" element={<AttendancePage />} />
            <Route path="ai-workforce" element={<AIWorkforcePage />} />
            <Route path="ai-assistant" element={<AIAssistantPage />} />
            <Route path="crm" element={<CRMDashboardPage />} />
            <Route path="dbs-tracking" element={<DBSTrackingPage />} />
            <Route path="ai-presentations" element={<AIPresentationPage />} />
            <Route path="meetings" element={<MeetingsPage />} />
            <Route path="meeting-room/:roomCode" element={<MeetingRoomPage />} />
            <Route path="meetings/:meetingId/minutes" element={<MeetingMinutesPage />} />
            <Route path="meeting-records" element={<MeetingRecordsPage />} />
            <Route path="rota" element={<RotaManagementPage />} />
            <Route path="parent-portal" element={<ParentDashboardPage />} />
            <Route path="parent-portal/payment-success" element={<PaymentSuccessPage />} />
            <Route path="parent-management" element={<ParentManagementPage />} />
            <Route path="feedback-management" element={<FeedbackManagementPage />} />
            <Route path="policy" element={<PolicyPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
